import { Settings01Icon } from '@langchain/untitled-ui-icons';
import { FormControl, FormLabel } from '@mui/joy';

import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { RadioGroup } from '@/components/RadioGroup/radio-group';
import useToast from '@/components/Toast/useToast';
import { Text } from '@/design-system';
import { Button } from '@/design-system/components/Button';
import {
  useOrganizations,
  useSetDefaultSSOProvisionOrgMutation,
} from '@/hooks/useSwr';

import { DefaultOrgSelectionCard } from './DefaultOrgSelectionCard';

interface DefaultOrgFormValues {
  selectedOrgId: string;
}

export function DefaultOrgModal() {
  'use no memo';
  const [open, setOpen] = useState(false);

  const { data: organizations } = useOrganizations();
  const defaultOrgId = organizations?.find(
    (org) => org.default_sso_provision
  )?.id;

  const setDefaultOrgMutation = useSetDefaultSSOProvisionOrgMutation();
  const { createToast } = useToast();

  const { handleSubmit, control, reset } = useForm<DefaultOrgFormValues>({
    defaultValues: {
      selectedOrgId: defaultOrgId || '',
    },
  });

  const onSubmit = async (formData: DefaultOrgFormValues) => {
    if (!formData.selectedOrgId) return;

    try {
      await setDefaultOrgMutation.trigger({
        headers: {
          'X-Organization-Id': formData.selectedOrgId,
        },
      });

      createToast({
        title: 'Success',
        description: 'Default SSO organization updated successfully',
        type: 'success',
      });

      setOpen(false);
    } catch {
      createToast({
        title: 'Error',
        description: 'Failed to set default organization',
        type: 'error',
      });
    }
  };

  const handleClose = () => {
    reset({
      selectedOrgId: defaultOrgId || '',
    });
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          title="Set Default Organization"
          childrenClassName="gap-6 max-h-[600px] flex flex-col"
        >
          <Text variant="h3">
            Select which organization new users will be added to by default.
          </Text>

          {setDefaultOrgMutation.error && (
            <ExpandableErrorAlert error={setDefaultOrgMutation.error} />
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col items-stretch gap-6"
          >
            <div className="flex min-h-0 flex-1 flex-col items-stretch gap-4">
              <FormControl className="flex min-h-0 flex-1 flex-col">
                <FormLabel className="text-sm font-normal text-tertiary">
                  Available Organizations
                </FormLabel>
                <Controller
                  name="selectedOrgId"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="min-h-0 flex-1 overflow-y-auto pr-2"
                    >
                      {organizations
                        ?.filter((org) => !org.is_personal)
                        .map((org) => (
                          <DefaultOrgSelectionCard
                            key={org.id}
                            org={org}
                            isSelected={field.value === org.id}
                          />
                        ))}
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-secondary pt-2">
              <Button
                type="submit"
                size="sm"
                loading={setDefaultOrgMutation.isMutating}
                disabled={setDefaultOrgMutation.isMutating}
              >
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Button
        onClick={() => {
          setOpen(true);
        }}
        variant="plain"
        leftDecorator={Settings01Icon}
        color="secondary"
        size="md"
        className="w-full justify-start pr-1"
      >
        Set Default Organization
      </Button>
    </>
  );
}
